// ─── Leave menu items (key matches LEAVE_GUIDES below) ──────────────────────

export const LEAVE_ITEMS = [
  {
    key: 'leave_types',
    label: 'Leave Types',
    path: '/leave_types',
    sidebar: 'Schedule → Leave Type',
    access_points: ['leave_type'],
  },
  {
    key: 'leaves',
    label: 'Leave / Off',
    path: '/leaves',
    sidebar: 'Schedule → Leave/Off',
    access_points: ['leave-off'],
  },
  {
    key: 'leave_report',
    label: 'Leave Report',
    path: '/detailedLeaveReport',
    sidebar: 'Reports → Detailed Leave',
    access_points: ['detailedLeaveReport'],
  },
  {
    key: 'leave_summary',
    label: 'Leave Summary',
    path: '/leavesummaryreport',
    sidebar: 'Reports → Leave Summary',
    access_points: ['leavesummaryreport'],
  },
  {
    key: 'detailed_leave',
    label: 'Detailed Leave Report',
    path: '/detailedLeaveReport',
    sidebar: 'Reports → Detailed Leave',
    access_points: ['detailedLeaveReport'],
  },
]

// ─── Full leave setup flow guide ─────────────────────────────────────────────

export const LEAVE_FLOW_GUIDE = `**Leave Setup — Full Flow**

Follow these steps in order to get leaves working correctly.

---

**Step 1 — Create Leave Types** *(Schedule → Leave Type)*
Define all leave categories (e.g. Annual Leave, Sick Leave, Casual Leave).
Set max allowed days, carry-forward rules, probation period, and payment status.

**Step 2 — Apply a Leave** *(Schedule → Leave/Off → Add)*
Select the employee (or branch/designation), leave type, start & end dates.
A leave request is created and sent for approval.

**Step 3 — Approve Leave Requests** *(Approvals → Leave Requests)*
HR/managers review pending requests and approve or reject them.
Approved leaves are counted against the employee's leave balance.

**Step 4 — View Leave Records** *(Schedule → Leave/Off)*
Browse all leave records, filter by employee, branch, date range, or status.

**Step 5 — Reports**
- *Leave Report* — per-employee leave transactions
- *Leave Summary* — balance summary per employee and leave type
- *Detailed Leave Report* — full breakdown with approval details`

// ─── Per-page guide texts (keyed by LEAVE_ITEMS key) ─────────────────────────

export const LEAVE_GUIDES = {
  leave_types: `**Leave Types**
📍 *Sidebar: Schedule → Leave Type*

Define the categories of leave available in your organisation.

**Key Fields:**
- **Type Name** — e.g. Annual Leave, Sick Leave, Casual Leave
- **Max Allowed Days** — maximum days per calculation period
- **Calculating Method** — monthly, yearly, or weekly accrual
- **Can Carry Forward** — whether unused days roll over to the next period
- **Payment Status** — paid (true) or unpaid (false)
- **Probation Period** — months before an employee can use this leave

**Note:** Leave types must be created before any leave can be applied.`,

  leaves: `**Leave / Off**
📍 *Sidebar: Schedule → Leave/Off*

Apply and manage leave requests for employees, branches, or designations.

**Fields:**
- Employee / Branch / Designation scope
- Leave Type (from Leave Types list)
- Start Date & End Date
- Half Day option
- Remarks / Attachment

**After applying**, the leave goes through the approval workflow (Approvals → Leave Requests).`,

  leave_report: `**Leave Report**
📍 *Sidebar: Reports → Leave Report*

Transaction-level view of all leaves taken by employees.

**Shows:**
- Employee name & ID
- Leave type, start & end dates
- Number of days, approval status
- Remarks

**Filters:** Date range, branch, employee, leave type, status`,

  leave_summary: `**Leave Summary Report**
📍 *Sidebar: Reports → Leave Summary*

Balance summary per employee and leave type for a selected period.

**Shows:**
- Total entitlement, used days, remaining balance
- Carry-forward brought in and sent out

**Filters:** Year, branch, employee, leave type`,

  detailed_leave: `**Detailed Leave Report**
📍 *Sidebar: Reports → Detailed Leave*

Full per-employee leave breakdown including approval details.

**Shows:**
- All leave fields including approver name and timestamp
- Paid/unpaid breakdown
- Attachment indicator

**Filters:** Date range, branch, employee, leave type`,
}

// ─── Leave issue diagnostic wizard steps ─────────────────────────────────────

export const LEAVE_ISSUE_STEPS = [
  {
    key: 'employee_id',
    label: 'Enter the **Employee ID** of the employee with the leave issue:',
  },
  {
    key: 'issue_field',
    label: 'Which field or aspect looks wrong? Select one:',
    options: [
      { label: 'Leave Status (pending/approved/rejected)', value: 'leave_status' },
      { label: 'Leave Balance / Days Remaining', value: 'leave_balance' },
      { label: 'Leave Days Count (too many or too few days)', value: 'leave_days_count' },
      { label: 'Paid / Unpaid Status', value: 'paid_status' },
      { label: 'Leave Type Assignment', value: 'leave_type' },
      { label: 'Leave Not Reflecting in Payroll', value: 'leave_payroll' },
      { label: 'Carry Forward Amount', value: 'carry_forward' },
    ],
  },
  {
    key: 'year',
    label: 'For which **Year**? Enter as `YYYY` (e.g. `2026`):',
  },
  {
    key: 'leave_type_name',
    label: 'Which leave type has the issue? Select one:',
    // options are populated dynamically after the year step
    dynamic: true,
  },
]
